// @flow

import {ClickControlBase} from './click_control_base';
import type {Options} from './click_control_base';
import {extend} from '../../util/util';
import {MapMouseEvent, MapTouchEvent} from '../events';
import {Places} from '../../services/places';
import Popup from '../popup';
import DOM from '../../util/dom';
import DwellTimesChart from '../../util/dwell_times_chart';
import {DwellTimes} from '../../services/dwelltimes';
import config from '../../util/config';
/**
 * The `PlaceClickControl` adds the ability to show a popup when a Place Icon is clicked on.
 *
 * @implements {IControl}
 *
 * @example
 * var placeClickControl = TrimbleMaps.PlaceClickControl();
 * map.addControl(placeClickControl);
 */
class PlaceClickControl extends ClickControlBase {
    _token: string;

    constructor(options: Options) {
        const opt = extend({
            layerId: 'places',
            clickLayerIds: [
                'places_point',
                'places_point_basic'
            ]
        }, options);
        super(opt);

        Places.authenticate().then((resp) => {
            this._token = JSON.parse(resp).access_token;
        }, (err) => {
            console.error(err);
        });
    }

    _createChart (globalResult: any, assetCategory: string): ?HTMLElement {
        const dtChart = new DwellTimesChart();
        const category = assetCategory.toLowerCase();
        const globalData = Array(24).fill(0);
        const timeItems = globalResult.hourlyAverageDwellTimes[category];
        const timeKeys = Object.keys(timeItems);
        let chartHTML = null;
        if (timeKeys.length > 0) {
            Object.entries(timeItems).forEach(
              ([key, value]) =>  {
                  // $FlowFixMe
                  globalData[Number(key.slice(-2))] = Math.round(value['minutes']);
              });
            let globalAverageDwellTime = '';
            if (globalResult.averageDwellTime[category] && globalResult.averageDwellTime[category] > 0) {
                const calculatedDwelltime = globalResult.averageDwellTime[category] / 60;
                const hour = Math.floor(calculatedDwelltime);
                const minutes = Math.round((calculatedDwelltime - hour) * 60);
                globalAverageDwellTime = hour === 0 ? `${minutes} minutes` : `${hour} hours, ${minutes} minutes`;
            }
            const footNote = globalAverageDwellTime ? `${globalAverageDwellTime}` : 'n/a';
            chartHTML = dtChart.createChart(globalData,
                {
                    barHeight: 100,
                    chartTitle: ` ${assetCategory} Average Dwell Time by Hour (in minutes)`,
                    dataColor: false,
                    dataColorBreaks: [20, 40],
                    dataColorValues: ['#368fc7', '#73b847', '#fbad26', '#e86363'],
                    footNote : `Overall Average Dwell Time: ${footNote}`,
                }
            );
        }
        return chartHTML;
    }

    _onClick(event: MapMouseEvent | MapTouchEvent) {
        // $FlowFixMe
        const features = event.features;
        if (features && features.length > 0) {
            if (features[0].properties && features[0].properties.tpid) {
                const placeId = features[0].properties.tpid;
                const getDetails = Places.getPlaceDetails({token: this._token, placeId});
                let dwellTimes;
                if (this._token) {
                    const configToken = config.API_TOKEN ? config.API_TOKEN.replace(/\s/g, '').replace('token=', '').replace('bearer=', '') :  null;
                    dwellTimes = DwellTimes.getGlobalDwellTimes({
                        token: configToken ? configToken : this._token,
                        placeId
                    });
                }
                Promise.all([getDetails, dwellTimes]).then((values) => {
                    const placeDetails = JSON.parse(values[0]);
                    const popupContentContainer = DOM.create('div', 'trimblemaps-ctrl-plpopup');
                    popupContentContainer.innerHTML = `
                        <h4>Location Details</h4>
                        <div class="trimblemaps-place-details-container">
                            <div class="trimblemaps-place-row">
                                <div class="trimblemaps-place-key">Name:</div>
                                <div class="trimblemaps-place-value">${placeDetails.name}</div>
                            </div>

                            ${this._formatAddress(placeDetails.formattedAddress)}

                            ${this._formatPhone(placeDetails.contactInfo.phone)}

                            ${this._formatUrl(placeDetails.contactInfo.url)}

                            ${this._formatOperatingHours(placeDetails.operatingHours)}

                            ${this._formatFacilityInfo(placeDetails.facilityInformation)}

                            ${this._formatCatName(placeDetails.categoryName)}
                        </div>`;

                    let maxWidth = 380;
                    if (typeof values[1] !== 'undefined') {
                        const globalResult = JSON.parse(values[1]);
                        if (globalResult && globalResult.data.length) {
                            const vehChart = this._createChart(globalResult.data[0], 'Vehicle');
                            if (vehChart !== null) {
                                maxWidth = 500;
                                popupContentContainer.appendChild(vehChart);
                            }
                        }
                    }

                    if (this._popup) {
                        this._popup.remove();
                    }
                    this._popup = new Popup({offset: 20, maxWidth: `${maxWidth}px`, closeButton: false})
                        .setLngLat(placeDetails.geometry.coordinates)
                        .setDOMContent(popupContentContainer);
                    if (this._options.showPopup) {
                        this._popup.addTo(this._map);
                    }
                    this._clickControlEventHandler();

                }, (err) => {
                    console.error(err);
                });
            }

            event.preventDefault();
        }
    }

    _formatOperatingHours(hours: Object) {
        if (!hours) return '';

        const hourString = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday'
        ].map((dayName) => `<div>${hours[dayName].formattedText}</div>`).join('');

        return `
        <div class="trimblemaps-place-row trimblemaps-place-accordion">
            <input type="checkbox" id="hours-toggle"/>
            <label class="trimblemaps-place-key" for="hours-toggle">
                <div class="trimblemaps-place-icon trimblemaps-place-clock"></div>
                Hours:</label>
            <label class="trimblemaps-place-value" for="hours-toggle">
                <div class="trimblemaps-place-icon trimblemaps-ctrl-icon-chevron-right"></div>
                <div class="trimblemaps-place-hours-container trimblemaps-place-accordion-content-shrink">${hourString}</div>
            </label>
        </div>`;
    }

    _formatCatName(catName: string) {
        if (!catName) return '';
        return `
        <div class="trimblemaps-place-row">
            <div class="trimblemaps-place-key">Category:</div>
            <div class="trimblemaps-place-value">${catName}</div>
        </div>`;
    }

    _formatAddress(address: string) {
        if (!address) return '';

        return `
        <div class="trimblemaps-place-row">
            <div class="trimblemaps-place-key">
                <div class="trimblemaps-place-icon trimblemaps-place-pin"></div>
                Address:
            </div>
        <div class="trimblemaps-place-value">${address}</div>
        </div>`;
    }
    _formatPhone(phone: string) {
        if (!phone) return '';

        return `
        <div class="trimblemaps-place-row">
            <div class="trimblemaps-place-key">
                <div class="trimblemaps-place-icon trimblemaps-place-phone"></div>
                Phone:
            </div>
            <div class="trimblemaps-place-value">${phone}</div>
        </div>`;
    }

    _formatUrl(url: string) {
        if (!url) return '';

        // ? should we check for http in address
        const urlString = (url) ? `<a href="https://${url}" target="_blank" rel="noopener">${url}</a>` : '';

        return `
            <div class="trimblemaps-place-row">
                <div class="trimblemaps-place-key">
                    <div class="trimblemaps-place-icon trimblemaps-place-globe"></div>
                    Website:
                </div>
                <div class="trimblemaps-place-value">${urlString}</div>
            </div>`;
    }

    _formatFacilityInfo(facilityInfo: Object) {
        if (!facilityInfo) return '';

        // fuel cost
        const fuel = (facilityInfo.fuel) ? `<div class="trimblemaps-place-amenities-grid-item"><div class="trimblemaps-place-amenities-icon trimblemaps-place-fuel"></div><div class="trimblemaps-place-amenities-text">Fuel</div></div>` : '';

        // parking
        const parking = (facilityInfo.parkingInformation) ? `<div class="trimblemaps-place-amenities-grid-item"><div class="trimblemaps-place-amenities-icon trimblemaps-place-parking"></div><div class="trimblemaps-place-amenities-text">Parking<br/>(${facilityInfo.parkingInformation.truckParkingSpaces})</div></div>` : '';

        // amenities
        const amenities = facilityInfo.amenities.map((a) => {
            const displayInfo = this._getAmenityDisplayInfo(a);
            return `<div class="trimblemaps-place-amenities-grid-item"><div class="trimblemaps-place-amenities-icon ${displayInfo.iconClass}"></div><div class="trimblemaps-place-amenities-text">${displayInfo.label}</div></div>`;
        });
        facilityInfo.amenities.map((a) => this._getAmenityDisplayInfo(a));

        return [`
            <div class="trimblemaps-place-row trimblemaps-place-accordion">
                <input type="checkbox" id="amenities-toggle"/>
                <label class="trimblemaps-place-key" for="amenities-toggle">Amenities:</label>
                <label class="trimblemaps-place-value" for="amenities-toggle">
                    <div class="trimblemaps-place-icon trimblemaps-ctrl-icon-chevron-right"></div>
                </label>
                <div class="trimblemaps-place-amenities-grid trimblemaps-place-accordion-content">`, fuel, parking, ...amenities, `</div></div>`].join('');

    }

    _getAmenityDisplayInfo(amenityId: string) {

        const displayInfo = {
            fuel: {label: 'Fuel', iconClass: 'trimblemaps-place-fuel'},
            parking: {label: 'Parking', iconClass: 'trimblemaps-place-parking'},
            atm: {label: 'ATM', iconClass: 'trimblemaps-place-atm'},
            hotelNearby: {label: 'Hotel Nearby', iconClass: 'trimblemaps-place-hotelNearby'},
            moneyServices: {label: 'Money Services', iconClass: 'trimblemaps-place-moneyServices'},
            permitServices: {label: 'Permit Services', iconClass: 'trimblemaps-place-permitServices'},
            restaurant: {label: 'Restaurant', iconClass: 'trimblemaps-place-restaurant'},
            scale: {label: 'Scale', iconClass: 'trimblemaps-place-scale'},
            showers: {label: 'Showers', iconClass: 'trimblemaps-place-showers'},
            transflo: {label: 'Transflo', iconClass: 'trimblemaps-place-transflo'},
            tripPak: {label: 'TripPak', iconClass: 'trimblemaps-place-tripPak'},
            truckWash: {label: 'Truck Wash', iconClass: 'trimblemaps-place-truckWash'},
            '24hourOpen': {label: 'Open 24 Hours', iconClass: 'trimblemaps-place-24hourOpen'},
            convenienceStore: {label: 'Convenience Store', iconClass: 'trimblemaps-place-convenienceStore'},
            laundry: {label: 'Laundry', iconClass: 'trimblemaps-place-laundry'},
            mailingServices: {label: 'Mailing Services', iconClass: 'trimblemaps-place-mailingServices'},
            rfid: {label: 'RF ID', iconClass: 'trimblemaps-place-rfid'},
            rvDump: {label: 'RV Dump', iconClass: 'trimblemaps-place-rvDump'},
            restrooms: {label: 'Restrooms', iconClass:'trimblemaps-place-restrooms'},
            wifi: {label: 'WiFi', iconClass: 'trimblemaps-place-wifi'},
        };

        // Fall back to placeholder image for unknown strings
        return displayInfo[amenityId] || {
            label: amenityId,
            iconClass: '',
        };
    }
}

export default PlaceClickControl;
